// Behavior-driven personalization engine — learns from patterns, auto-adjusts decisions

import { loadMemory, saveMemory, type UserMemory } from "./userMemory";
import { getHistoryEntries, getBuyingProfile } from "./watchlistStore";
import { getOutcomeSummary } from "./outcomeTracker";

export interface BehaviorProfile {
  priceSensitivity: "high" | "medium" | "low"; // always chooses cheapest?
  speedPreference: "high" | "medium" | "low"; // always chooses fastest?
  riskTolerance: "conservative" | "moderate" | "adventurous";
  localBias: "strong_local" | "neutral" | "international";
  autoAdjusted: boolean;
  adjustmentLabel: string; // "Adjusted based on your past 12 decisions"
  suggestedPriority?: string;
  decisionsAnalyzed: number;
}

export function analyzeBehavior(): BehaviorProfile {
  const history = getHistoryEntries();
  const outcomes = getOutcomeSummary();
  const profile = getBuyingProfile();
  const mem = loadMemory();

  const total = history.length;
  const defaultProfile: BehaviorProfile = {
    priceSensitivity: "medium",
    speedPreference: "medium",
    riskTolerance: "moderate",
    localBias: "neutral",
    autoAdjusted: false,
    adjustmentLabel: "",
    decisionsAnalyzed: total,
  };

  if (total < 3) return defaultProfile;

  // Analyze label patterns
  const labels = history.map((h) => h.winnerLabel);
  const valueCount = labels.filter((l) => l === "Best Value").length;
  const speedCount = labels.filter((l) => l === "Fastest Delivery").length;
  const premiumCount = labels.filter((l) => l === "Premium Pick").length;

  const valueRatio = valueCount / total;
  const speedRatio = speedCount / total;
  const premiumRatio = premiumCount / total;

  // Determine price sensitivity
  const priceSensitivity: BehaviorProfile["priceSensitivity"] =
    valueRatio > 0.5 ? "high" : premiumRatio > 0.4 ? "low" : "medium";

  // Determine speed preference
  const speedPreference: BehaviorProfile["speedPreference"] =
    speedRatio > 0.4 ? "high" : speedRatio < 0.1 ? "low" : "medium";

  // Determine risk tolerance
  const riskTolerance: BehaviorProfile["riskTolerance"] =
    premiumRatio > 0.3 ? "adventurous" : valueRatio > 0.5 ? "conservative" : "moderate";

  // Local bias from memory
  const localBias: BehaviorProfile["localBias"] =
    mem.localPreference === "local" ? "strong_local" :
    mem.localPreference === "international" ? "international" : "neutral";

  // Auto-adjust
  let suggestedPriority = mem.preferredPriority;
  let autoAdjusted = false;

  if (priceSensitivity === "high" && mem.preferredPriority !== "price") {
    suggestedPriority = "price";
    autoAdjusted = true;
  } else if (speedPreference === "high" && mem.preferredPriority !== "speed") {
    suggestedPriority = "speed";
    autoAdjusted = true;
  } else if (priceSensitivity === "low" && mem.preferredPriority !== "quality") {
    suggestedPriority = "quality";
    autoAdjusted = true;
  }

  // Apply auto-adjustment to memory
  if (autoAdjusted && suggestedPriority) {
    const updated = loadMemory();
    updated.preferredPriority = suggestedPriority;
    saveMemory(updated);
  }

  const adjustmentLabel = autoAdjusted
    ? `Adjusted automatically based on your past ${total} decisions`
    : total >= 3 ? `Personalized from ${total} past decisions` : "";

  return {
    priceSensitivity,
    speedPreference,
    riskTolerance,
    localBias,
    autoAdjusted,
    adjustmentLabel,
    suggestedPriority,
    decisionsAnalyzed: total,
  };
}

// Human-readable personalization explanations
export function getPersonalizationExplanation(): string[] {
  const history = getHistoryEntries();
  const total = history.length;
  if (total < 3) return [];

  const explanations: string[] = [];
  const labels = history.map((h) => h.winnerLabel);
  const valueCount = labels.filter((l) => l === "Best Value").length;
  const speedCount = labels.filter((l) => l === "Fastest Delivery").length;
  const premiumCount = labels.filter((l) => l === "Premium Pick").length;

  if (valueCount > total * 0.4) {
    explanations.push(`Your decisions prioritize price over speed (${valueCount} of ${total} chose Best Value)`);
  }
  if (speedCount > total * 0.3) {
    explanations.push(`You tend toward fast delivery (${speedCount} of ${total} chose Fastest Delivery)`);
  }
  if (premiumCount > total * 0.25) {
    explanations.push(`You lean toward premium options (${premiumCount} of ${total} chose Premium Pick)`);
  }

  const mem = loadMemory();
  if (mem.localPreference === "local") {
    explanations.push("You prefer local retailers when available");
  }

  return explanations;
}

// Category affinity from history
export function getCategoryAffinity(): { category: string; count: number }[] {
  const history = getHistoryEntries();
  if (history.length < 2) return [];

  const counts: Record<string, number> = {};
  history.forEach((h) => {
    if (h.category) counts[h.category] = (counts[h.category] || 0) + 1;
  });

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([category, count]) => ({ category, count }));
}

// Should we skip clarification for this user?
export function shouldAutoResolve(): boolean {
  const mem = loadMemory();
  const history = getHistoryEntries();
  return history.length >= 5 && !!mem.preferredPriority;
}

// Get clarification reduction level
export function getClarificationReduction(): "none" | "partial" | "full" {
  const history = getHistoryEntries();
  if (history.length >= 8) return "full";
  if (history.length >= 4) return "partial";
  return "none";
}
